import { isVideoLegacySeason } from "~/components/blocks/product/type-guard";
import { runningInBrowser } from "~/const/browser";
import type { G_GetVideoQuery } from "~/lib/graphql";
import { platform } from "~/lib/navigator/user-agent";
import { useAuth } from "~/provider/auth";

const APP_URL = {
  ANDROID: {
    INSTALL: "https://play.google.com/store/apps/details?id=com.dmm.app.movieplayer",
    SCHEME: "https://www.dmm.com/app/-/movieplayer/intent?action=start",
  },
  ANDROID_VR: {
    INSTALL: "https://portalapp.dmm.com/dmmvrplayer/DMMVRPlayer.apk",
  },
  IOS: {
    INSTALL: "itms-apps://itunes.apple.com/app/id663294738",
    SCHEME: "dmmplayer://",
  },
  IOS_VR: {
    INSTALL:
      "https://itunes.apple.com/us/app/dmm-vr-dong-huapureiya/id1134925341?l=ja&ls=1&mt=8&_gl=1*16491en*rieSh3Ee_ga*MTQzOTI1MjYzMi4xNjQ0MjExMjEw*rieSh3Ee_ga_KQYE0DE5JW*MTY2ODE1MzcwMy41LjEuMTY2ODE1NjUwNC4wLjAuMA..*_fplc*OFB3ZjJpZlJrbHpHWloxdVc5ZDVDM2JLRk1uYll4T1kyNGU0QXhnZGVCZ0lVY1RLRVNkNUZ1NkE5TWhMbWo1QnlGTEpBanNLeVZ3c1FzMDhrdFZITFRmaHBLUEZWZmJEWGRRVlVJZTFvSlNZN2VTMHhGbENxb3kxTFBEYzBRJTNEJTNE",
  },
  DMM_TV_ANDROID: {
    INSTALL: "https://play.google.com/store/apps/details?id=com.dmm.premium",
    SCHEME: "dmmtv://video/",
  },
  DMM_TV_IOS: {
    INSTALL: "https://apps.apple.com/jp/app/id1584918838",
    SCHEME: "dmmtv://video/",
  },
} as const;

type UseApplicationHandlerArgs = { videoData: G_GetVideoQuery["video"] };

export const useApplicationHandler = (args: UseApplicationHandlerArgs) => {
  const { videoData } = args;

  const { isLoggedIn } = useAuth();

  const applicationDmmTvInstall = () => {
    if (!runningInBrowser) return;

    if (platform === "Android") {
      location.href = APP_URL.DMM_TV_ANDROID.INSTALL;
      return;
    }

    if (platform === "iOS") {
      location.href = APP_URL.DMM_TV_IOS.INSTALL;
      return;
    }
  };

  /**
   * ### アプリを起動ボタン押下時にDMM TVアプリをスキーム起動させるための関数
   * @param seasonId 必須 : シーズンIDだけ存在する場合は指定したシーズンのビデオ末端を開く
   * @param contentId 必須 : コンテンツIDも存在する場合は指定したエピソードのビデオ末端を開く
   * @see https://confl.arms.dmm.com/pages/viewpage.action?pageId=1289111900
   */
  const applicationDmmTvScheme = (seasonId: string, contentId: string | undefined) => {
    if (!runningInBrowser) return;

    const schemeUrl =
      contentId !== undefined && contentId !== ""
        ? `episode?season_id=${seasonId}&episode_id=${contentId}`
        : `season?id=${seasonId}`;

    const loginUrl = isLoggedIn ? "&loggedIn=1" : "&loggedIn=0";

    if (platform === "Android") {
      location.href = `${APP_URL.DMM_TV_ANDROID.SCHEME}${schemeUrl}${loginUrl}`;
      return;
    }

    if (platform === "iOS") {
      location.href = `${APP_URL.DMM_TV_IOS.SCHEME}${schemeUrl}${loginUrl}`;
      return;
    }
  };

  const application2DInstall = () => {
    if (!runningInBrowser) return;

    if (platform === "Android") {
      location.href = APP_URL.ANDROID.INSTALL;
      return;
    }

    if (platform === "iOS") {
      location.href = APP_URL.IOS.INSTALL;
      return;
    }
  };

  const application2DScheme = () => {
    if (!runningInBrowser) return;

    if (platform === "Android") {
      location.href = APP_URL.ANDROID.SCHEME;
      return;
    }

    if (platform === "iOS") {
      location.href = APP_URL.IOS.SCHEME;
      return;
    }
  };

  const applicationVrInstall = () => {
    if (!runningInBrowser) return;

    if (platform === "Android") {
      location.href = APP_URL.ANDROID_VR.INSTALL;
      return;
    }

    if (platform === "iOS") {
      location.href = APP_URL.IOS_VR.INSTALL;
      return;
    }
  };

  const applicationVrScheme = () => {
    if (!runningInBrowser) return;

    if (isVideoLegacySeason(videoData)) {
      const { iosURLSchemes, androidURLSchemes } = videoData.content.viewingRights;

      // MEMO（kimura）: 端末判定がiOS、かつ、スキームURLが取得できた場合は「アプリで再生ボタン」を表示する
      if (platform === "iOS") {
        if (iosURLSchemes !== undefined && iosURLSchemes !== null && iosURLSchemes.length > 0) {
          const schemeUrl = iosURLSchemes[0].url;
          location.href = schemeUrl;
          return;
        }
      }

      // MEMO（kimura）: 端末判定がAndroid、かつ、スキームURLが取得できた場合は「アプリで再生ボタン」を表示する
      if (platform === "Android") {
        if (androidURLSchemes !== undefined && androidURLSchemes !== null && androidURLSchemes.length > 0) {
          const schemeUrl = androidURLSchemes[0].url;
          location.href = schemeUrl;
          return;
        }
      }
    }

    return;
  };

  return {
    /** グラビア・VR作品以外の「アプリをインストール」ボタンを押下した場合のイベントハンドラ */
    applicationDmmTvInstall,
    /** グラビア・VR作品以外の「アプリを起動」ボタンを押下した場合のイベントハンドラ */
    applicationDmmTvScheme,
    /** 2D作品の「アプリをインストール」ボタンを押下した場合のイベントハンドラ */
    application2DInstall,
    /** 2D作品の「アプリを起動」ボタンを押下した場合のイベントハンドラ */
    application2DScheme,
    /** VR作品の「アプリをインストール」ボタンを押下した場合のイベントハンドラ */
    applicationVrInstall,
    /** VR作品の「アプリを起動」ボタンを押下した場合のイベントハンドラ */
    applicationVrScheme,
  };
};
